import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  Output,
  EventEmitter,
  HostBinding,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  afterNextRender,
  Injector,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { Actions, Store, ofActionSuccessful } from '@ngxs/store';
import {
  distinctUntilChanged,
  filter,
  Observable,
  startWith,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { ModuleNumbers } from 'src/app/core/enums/module-numbers.enum';
import { MenuItemModel } from 'src/app/core/models/menu-id.model';
import { CreateUpdateDeleteRecursiveArrayService } from 'src/app/core/services/create-update-delete-recursive-array.service';
import { MenuStructureNavigationService } from 'src/app/core/services/menu-structure-navigation.service';
import { MenuListState } from 'src/app/core/state/menu-list.state';
import { MenuTreeStructureState } from 'src/app/core/state/menu-tree-structure.state';
import { ComponentsStructureState } from 'src/app/maintenance-admin/state/components-structure.state';
import { CdkDropList, CdkDropListGroup } from '@angular/cdk/drag-drop';
import {
  GetMaintenanceMenuStructure,
  TransferMaintenanceComponent,
  UpdateComponentsStructureOrder,
} from 'src/app/maintenance-admin/state/components-structure.actions';
import { ALL_DEPS_TOKEN } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { MenuDragDropService } from '../menu-drag-drop.service';
import {
  LoadSpecificMenuStructure,
  LoadSpecificMenuStructureForTenant,
  SetActiveMenuElement,
  UpdateMenuStructureOrder,
} from '../../../core/state/menu-tree-structure.actions';

const TENANT_WIDE_MODULES = [ModuleNumbers.StandardJobTemplates];
const MODULES_WITHOUT_MENUS = [ModuleNumbers.Order, ModuleNumbers.CrewManagement];
const MENU_ITEMS_PER_PAGE = 30;

interface MenuNodeIndexEntry {
  item: MenuItemModel;
  path: string;
  rootId: string;
}

function getMenuNodeIndex(
  items: MenuItemModel[],
  ancestors: MenuItemModel[] = []
): MenuNodeIndexEntry[] {
  return items.flatMap((item) => [
    {
      item,
      path: ancestors.map((ancestor) => ancestor.name).join(' › '),
      rootId: ancestors[0]?.id ?? item.id,
    },
    ...getMenuNodeIndex(item.children ?? [], [...ancestors, item]),
  ]);
}

@Component({
  selector: 'app-menu-tree-structure',
  templateUrl: './menu-tree-structure.component.html',
  styleUrls: ['./menu-tree-structure.component.scss'],
  standalone: false,
})
export class MenuTreeStructureComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  getEntireMenuStructure$: Observable<MenuItemModel[]> = this.store
    .select(MenuTreeStructureState.getEntireMenuStructure)
    .pipe(
      tap((data) => {
        this.menuDNDService.setInitialMenuStructure(data);
      })
    );
  getModuleNumber$: Observable<number | undefined> = this.store.select(
    MenuListState.getModuleNumber
  );
  maintenanceMenuStructure$: Observable<any[]> = this.store
    .select(ComponentsStructureState.getComponentsStructure)
    .pipe(
      tap((data) => {
        this.menuDNDService.setInitialMenuStructure(data as any[]);
      })
    );

  structurePreview: MenuItemModel[] = [];
  activeElementId$: Observable<string> = this.store.select(
    MenuTreeStructureState.activeMenuElementId
  );
  loaded$: Observable<boolean> = this.store.select(MenuTreeStructureState.menuStructureLoaded);
  actions = [UpdateComponentsStructureOrder, UpdateMenuStructureOrder];

  @Input() showFunctionalButtons = true;
  @Input() isRootAddEnabled = false;
  @Input() menuItemsAsRouting = false;
  @Input() moduleNumber: ModuleNumbers;
  @Input() menuMode = false;
  @Input() depId: string;
  @Input() hideIfNoItems = false;
  @Input() adminPrivilege: string;
  @Input() searchEnabled = false;
  @ViewChild(CdkDropListGroup) listGroup: CdkDropList<CdkDropListGroup<any>>;
  @ViewChild('scrollContainer') scrollContainerRef: ElementRef<HTMLElement>;
  @Output() readonly menuItemClicked: EventEmitter<string> = new EventEmitter<string>();
  @Output() readonly menuItemSelected = new EventEmitter<void>();

  visibleTopLevelCount = MENU_ITEMS_PER_PAGE;
  visibleSearchResultCount = MENU_ITEMS_PER_PAGE;
  readonly searchControl = new FormControl('', { nonNullable: true });
  searchResults: MenuNodeIndexEntry[] = [];
  // Increment on sidebar reopen and propagate through the tree to reveal an unchanged selection.
  selectionRevealVersion = 0;
  private menuNodeIndex: MenuNodeIndexEntry[] = [];
  scrollContainerMaxHeightPx: number | null = null;
  contentExceedsContainer = false;
  private scrollHandler: (() => void) | null = null;
  private lastLoadMoreEmit = 0;
  private readonly loadMoreThrottleMs = 300;
  private resizeObserver: ResizeObserver | null = null;
  private mutationObserver: MutationObserver | null = null;
  private lastStructureSignature: string | null = null;
  private scrollToTopOnNextLayout = false;
  private isStructureLoaded = false;
  private displayedTreeModule: ModuleNumbers | null = null;

  @HostBinding('class.menu-tree-structure-hidden') get hideIfNoItemsBinding() {
    return this.hideIfNoItems && this.structurePreview.length === 0;
  }

  get shouldUseInfiniteScroll(): boolean {
    return this.menuMode && this.menuItemsAsRouting;
  }

  get isSearching(): boolean {
    return this.searchEnabled && !!this.searchControl.value.trim();
  }

  get visibleStructurePreview(): MenuItemModel[] {
    if (!this.shouldUseInfiniteScroll) {
      return this.structurePreview;
    }
    return this.structurePreview.slice(0, this.visibleTopLevelCount);
  }

  get visibleSearchResults(): MenuNodeIndexEntry[] {
    return this.searchResults.slice(0, this.visibleSearchResultCount);
  }

  get hasMoreItems(): boolean {
    return (
      this.shouldUseInfiniteScroll &&
      (this.isSearching
        ? this.searchResults.length > this.visibleSearchResultCount
        : this.structurePreview.length > this.visibleTopLevelCount)
    );
  }

  get shouldShowLoader(): boolean {
    if (this.isStructureLoaded) {
      return false;
    }
    return (
      this.showFunctionalButtons ||
      this.structurePreview.length === 0 ||
      this.displayedTreeModule !== this.module
    );
  }

  readonly ALL_DEPS_TOKEN = ALL_DEPS_TOKEN;
  tenantId: string;
  module: number;
  moduleNumbers = ModuleNumbers;
  chosenElementId = '';
  isProductOwner = false;

  private subscriptions = new Subscription();

  constructor(
    private store: Store,
    private router: Router,
    public menuDNDService: MenuDragDropService,
    private actions$: Actions,
    private elementRef: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef,
    private menuStructureNavigationService: MenuStructureNavigationService,
    private injector: Injector
  ) {
    this.subscriptions.add(
      this.menuDNDService.structurePreview.subscribe((structure) => {
        this.structurePreview = structure;
        this.menuNodeIndex = this.shouldUseInfiniteScroll ? getMenuNodeIndex(structure) : [];
        this.updateSearchResults();
        if (this.shouldUseInfiniteScroll) {
          const signature = this.getStructureSignature(structure);
          if (this.lastStructureSignature !== signature) {
            this.lastStructureSignature = signature;
            this.visibleTopLevelCount = MENU_ITEMS_PER_PAGE;
            this.visibleSearchResultCount = MENU_ITEMS_PER_PAGE;
            this.scrollToTopOnNextLayout = true;
          }
          this.includeSelectedRoot();
          setTimeout(() => {
            this.addScrollListener();
            this.updateScrollContainerHeight();
            this.setupResizeObserver();
            if (this.scrollToTopOnNextLayout && this.scrollContainerRef?.nativeElement) {
              this.scrollToTopOnNextLayout = false;
              this.scrollSelectionIntoView(true);
            }
          }, 150);
        }
      })
    );
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.searchControl.valueChanges.subscribe(() => {
        this.visibleSearchResultCount = MENU_ITEMS_PER_PAGE;
        this.lastLoadMoreEmit = 0;
        this.updateSearchResults();
        if (this.scrollContainerRef?.nativeElement) {
          this.scrollContainerRef.nativeElement.scrollTop = 0;
        }
        if (!this.isSearching) {
          this.scrollSelectionIntoView();
        }
      })
    );
    this.subscriptions.add(
      this.loaded$.subscribe((loaded) => {
        this.isStructureLoaded = loaded;
        if (loaded) {
          this.scrollSelectionIntoView();
        }
      })
    );
    this.subscriptions.add(
      this.store.select(MenuTreeStructureState.getDisplayedTreeModule).subscribe((module) => {
        this.displayedTreeModule = module;
      })
    );
    this.subscriptions.add(
      this.activeElementId$.pipe(distinctUntilChanged()).subscribe((activeElementId) => {
        this.chosenElementId = activeElementId;
        this.includeSelectedRoot();
        this.scrollSelectionIntoView();
      })
    );
    this.subscriptions.add(
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          startWith(null),
          switchMap(() => this.getEntireMenuStructure$),
          filter((data) => !!data.length),
          tap((data) => {
            const routeElementId = this.isAnyElementChosen(data, this.router.url);
            if (routeElementId) {
              this.store.dispatch(new SetActiveMenuElement(routeElementId));
              return;
            }
            if (this.menuStructureNavigationService.isViewAllRoute()) {
              this.store.dispatch(new SetActiveMenuElement(''));
            }
          })
        )
        .subscribe()
    );
    this.subscriptions.add(
      this.getModuleNumber$.pipe(distinctUntilChanged()).subscribe((moduleNumber) => {
        if (
          moduleNumber &&
          (moduleNumber !== ModuleNumbers.Document || !this.showFunctionalButtons)
        ) {
          this.module = moduleNumber;
          this.searchControl.reset();
          if (TENANT_WIDE_MODULES.includes(this.module)) {
            this.store.dispatch(new LoadSpecificMenuStructureForTenant(this.module));
          } else if (!MODULES_WITHOUT_MENUS.includes(this.module)) {
            this.store.dispatch(new LoadSpecificMenuStructure(this.module));
          }
        }
      })
    );
    this.subscriptions.add(
      (this.moduleNumber === ModuleNumbers.Maintenance
        ? this.maintenanceMenuStructure$
        : this.getEntireMenuStructure$
      ).subscribe()
    );
    this.subscriptions.add(
      this.actions$.pipe(ofActionSuccessful(TransferMaintenanceComponent)).subscribe(() => {
        this.store.dispatch(new GetMaintenanceMenuStructure());
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.depId || changes.searchEnabled) {
      this.searchControl.reset();
    }
  }

  ngAfterViewInit(): void {
    if (this.shouldUseInfiniteScroll && this.structurePreview.length > 0) {
      setTimeout(() => {
        this.addScrollListener();
        this.updateScrollContainerHeight();
        this.setupResizeObserver();
      }, 100);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.mutationObserver?.disconnect();
    this.mutationObserver = null;
    this.removeScrollListener();
    this.subscriptions.unsubscribe();
  }

  onSidebarOpening(): void {
    this.searchControl.reset();
    this.selectionRevealVersion++;
  }

  onSidebarOpened(): void {
    this.scrollSelectionIntoView();
  }

  private scrollSelectionIntoView(resetScrollToTop = false): void {
    if (!this.shouldUseInfiniteScroll) {
      return;
    }
    afterNextRender(
      () => {
        // Apply the new height before scrolling, including when clearing search restores All.
        this.updateScrollContainerHeight(() => this.scrollSelectedRow(resetScrollToTop));
      },
      { injector: this.injector }
    );
  }

  private scrollSelectedRow(resetScrollToTop: boolean): void {
    const container = this.scrollContainerRef?.nativeElement;
    if (!container) {
      return;
    }
    if (resetScrollToTop) {
      container.scrollTop = 0;
    }
    if (
      !this.elementRef.nativeElement.closest('.mat-drawer-opened') ||
      this.isSearching ||
      this.shouldShowLoader
    ) {
      return;
    }
    const selected = container.querySelector<HTMLElement>('[aria-current="true"]');
    if (!selected) {
      return;
    }
    const bounds = container.getBoundingClientRect();
    const selectionBounds = selected.getBoundingClientRect();
    // Keep the structure header and surrounding drawers fixed while revealing the row.
    container.scrollBy({
      top:
        selectionBounds.top < bounds.top
          ? selectionBounds.top - bounds.top
          : Math.max(0, selectionBounds.bottom - bounds.top - container.clientHeight),
      left:
        selectionBounds.left < bounds.left
          ? selectionBounds.left - bounds.left
          : Math.max(0, selectionBounds.right - bounds.left - container.clientWidth),
    });
  }

  private updateSearchResults(): void {
    const query = this.searchControl.value.trim().toLocaleLowerCase();
    this.searchResults = query
      ? this.menuNodeIndex.filter(({ item }) => item.name.toLocaleLowerCase().includes(query))
      : [];
    this.cdr.markForCheck();
  }

  private includeSelectedRoot(): void {
    const selected = this.menuNodeIndex.find(({ item }) => item.id === this.chosenElementId);
    const rootIndex = this.structurePreview.findIndex((item) => item.id === selected?.rootId);
    this.visibleTopLevelCount = Math.max(this.visibleTopLevelCount, rootIndex + 1);
  }

  private loadMoreItems(): void {
    if (this.isSearching) {
      this.visibleSearchResultCount += MENU_ITEMS_PER_PAGE;
    } else {
      this.visibleTopLevelCount += MENU_ITEMS_PER_PAGE;
    }
    setTimeout(() => this.updateScrollContainerHeight(), 50);
  }

  async viewAll(): Promise<void> {
    const module =
      this.module ?? this.store.selectSnapshot(MenuTreeStructureState.getCurrentModuleNumber);
    const outcome = await this.menuStructureNavigationService.selectAll(
      module,
      this.chosenElementId
    );
    if (this.menuStructureNavigationService.isCompleted(outcome)) {
      this.menuItemClicked.emit('');
      this.menuItemSelected.emit();
    }
  }

  private setupResizeObserver(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    const parent = this.elementRef.nativeElement.closest('.mat-drawer-inner-container');
    const container = this.scrollContainerRef?.nativeElement;
    if (!parent || !container || !this.shouldUseInfiniteScroll) {
      return;
    }
    this.resizeObserver = new ResizeObserver(() => {
      this.updateScrollContainerHeight();
    });
    this.resizeObserver.observe(parent);
    this.mutationObserver = new MutationObserver(() => {
      setTimeout(() => this.updateScrollContainerHeight(), 50);
    });
    this.mutationObserver.observe(container, {
      childList: true,
      subtree: true,
    });
  }

  private updateScrollContainerHeight(afterLayout?: () => void): void {
    if (!this.shouldUseInfiniteScroll) {
      return;
    }
    const parent = this.elementRef.nativeElement.closest('.mat-drawer-inner-container');
    const container = this.scrollContainerRef?.nativeElement;
    // A hidden Material drawer has no layout; preserve its last measured height.
    if (!parent || !container || container.getClientRects().length === 0) {
      return;
    }
    const bottom = Math.min(parent.getBoundingClientRect().bottom, window.innerHeight);
    const availableHeight = bottom - container.getBoundingClientRect().top;
    const maxHeight = Math.max(0, availableHeight);
    requestAnimationFrame(() => {
      this.scrollContainerMaxHeightPx = maxHeight;
      this.contentExceedsContainer = container.scrollHeight > maxHeight;
      this.cdr.markForCheck();
      if (afterLayout) {
        afterNextRender(afterLayout, { injector: this.injector });
      }
    });
  }

  private addScrollListener(): void {
    this.removeScrollListener();
    setTimeout(() => {
      const container = this.scrollContainerRef?.nativeElement;
      if (!container || this.scrollHandler) {
        return;
      }
      this.scrollHandler = () => this.onScroll();
      container.addEventListener('scroll', this.scrollHandler);
    }, 0);
  }

  private removeScrollListener(): void {
    const container = this.scrollContainerRef?.nativeElement;
    if (container && this.scrollHandler) {
      container.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }
  }

  private onScroll(): void {
    const container = this.scrollContainerRef?.nativeElement;
    if (!this.hasMoreItems || !container) {
      return;
    }
    const { scrollTop, clientHeight, scrollHeight } = container;
    const threshold = 50;
    if (scrollTop + clientHeight >= scrollHeight - threshold) {
      const now = Date.now();
      if (now - this.lastLoadMoreEmit < this.loadMoreThrottleMs) {
        return;
      }
      this.lastLoadMoreEmit = now;
      this.loadMoreItems();
    }
  }

  highlightMenuItem(itemId: string) {
    if (!this.showFunctionalButtons) {
      this.chosenElementId = itemId;
    }
    this.menuItemClicked.emit(itemId);
    if (itemId) {
      this.menuItemSelected.emit();
    }
  }

  private isAnyElementChosen(items: MenuItemModel[], url: string): string {
    const flatItems = CreateUpdateDeleteRecursiveArrayService.getFlatList(items);
    return flatItems.find((item) => url.includes(item.id))?.id || '';
  }

  private getStructureSignature(structure: MenuItemModel[]): string {
    if (!structure?.length) {
      return '';
    }
    const first = structure[0]?.id ?? '';
    const last = structure[structure.length - 1]?.id ?? '';
    return `${structure.length}:${first}:${last}`;
  }
}
